import React from 'react';
import styled from 'styled-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { GqlMobileCustomization, MobileNavigationStyle } from 'types/graphql';
import {
  AccountIcon,
  CartIcon,
  ConciergeIcon,
  LoyaltyIcon,
  MoreIcon,
  PayIcon,
  ShopFilledIcon,
  SpecialsIcon,
} from './mobile-tab-icons';

type TabIconProps = {
  color?: string;
  icon: JSX.Element;
  label: string;
  showLabels: boolean;
};

const TabIcon = ({ color, icon, label, showLabels }: TabIconProps): JSX.Element => (
  <TabIconContainer color={color}>
    {icon}
    {showLabels && label}
  </TabIconContainer>
);

const valueOrDefault = (value: string | null | undefined, defaultValue: string): string =>
  value?.length ? value : defaultValue;

// Behind the ecomm.app.advanced-customization flag: Solid is the only
// style that shows a solid tab bar background — Liquid Glass floats over
// content and ignores bottomBarBackgroundColor, and Platform Default renders
// using the Liquid Glass display in this preview (the admin can't know the
// end-user's OS, so it mirrors the modern default). With the flag off this
// always renders the pre-ECOMM-12583 solid tab bar, ignoring bottomBarStyle.
const isSolidStyle = (style?: MobileNavigationStyle | null): boolean => style === MobileNavigationStyle.solid;

const TabBar = ({ mobileCustomization }: { mobileCustomization?: GqlMobileCustomization | null }): JSX.Element => {
  const flags = useFlags();
  const canShowPay = flags['fintech.retail-payments.scan-to-pay.rollout'] ?? false;
  const showRedesign = flags['ecomm.app.v2-redesign'] ?? false;
  const advancedCustomizationEnabled = flags['ecomm.app.advanced-customization'] ?? false;
  const showSpecials = mobileCustomization?.tabDisplay?.showSpecials ?? false;
  const showPay = mobileCustomization?.tabDisplay?.showPay ?? true;
  const showCart = mobileCustomization?.tabDisplay?.showCart ?? true;
  const showLoyalty = mobileCustomization?.tabDisplay?.showLoyalty ?? true;
  const showLabels = (mobileCustomization?.showTabLabels ?? false) || !showRedesign;
  const showConcierge = mobileCustomization?.tabDisplay?.showConcierge ?? false;
  const isSolid = isSolidStyle(mobileCustomization?.bottomBarStyle);
  const isLiquidGlass = advancedCustomizationEnabled && !isSolid;

  return (
    <TabBarContainer
      $isLiquidGlass={isLiquidGlass}
      backgroundColor={valueOrDefault(mobileCustomization?.bottomBarBackgroundColor, '#FFFFFF')}
      color={valueOrDefault(mobileCustomization?.bottomBarInactiveColor, '#4F5D68')}
    >
      <TabIcon
        color={valueOrDefault(mobileCustomization?.bottomBarActiveColor, '#0B99E6')}
        icon={<ShopFilledIcon />}
        label={valueOrDefault(mobileCustomization?.tabLabels?.shop, 'Shop')}
        showLabels={showLabels}
      />
      {(!showRedesign || showSpecials) && (
        <TabIcon
          icon={<SpecialsIcon />}
          label={valueOrDefault(mobileCustomization?.tabLabels?.specials, 'Specials')}
          showLabels={showLabels}
        />
      )}
      {canShowPay && (!showRedesign || showPay) && (
        <TabIcon
          icon={<PayIcon />}
          label={valueOrDefault(mobileCustomization?.tabLabels?.pay, 'Pay')}
          showLabels={showLabels}
        />
      )}
      {(!showRedesign || showCart) && (
        <TabIcon
          icon={<CartIcon />}
          label={valueOrDefault(mobileCustomization?.tabLabels?.cart, 'Cart')}
          showLabels={showLabels}
        />
      )}
      {showRedesign && showConcierge && (
        <TabIcon
          icon={<ConciergeIcon />}
          label={valueOrDefault(mobileCustomization?.tabLabels?.concierge, 'Concierge')}
          showLabels={showLabels}
        />
      )}
      {showRedesign && showLoyalty && (
        <TabIcon
          icon={<LoyaltyIcon />}
          label={valueOrDefault(mobileCustomization?.tabLabels?.loyalty, 'Loyalty')}
          showLabels={showLabels}
        />
      )}
      {!showRedesign && (
        <TabIcon
          icon={<AccountIcon />}
          label={valueOrDefault(mobileCustomization?.tabLabels?.account, 'Account')}
          showLabels={showLabels}
        />
      )}
      {showRedesign && (
        <TabIcon
          icon={<MoreIcon />}
          label={valueOrDefault(mobileCustomization?.tabLabels?.more, 'More')}
          showLabels={showLabels}
        />
      )}
    </TabBarContainer>
  );
};

const TabBarContainer = styled.div<{ $isLiquidGlass: boolean; backgroundColor: string; color: string }>`
  background-color: ${({ $isLiquidGlass, backgroundColor }) => ($isLiquidGlass ? 'transparent' : backgroundColor)};
  backdrop-filter: ${({ $isLiquidGlass }) => ($isLiquidGlass ? 'blur(15px) saturate(180%)' : 'none')};
  border-radius: ${({ $isLiquidGlass }) => ($isLiquidGlass ? '100px' : '0')};
  color: ${({ color }) => color};
  display: flex;
  justify-content: space-between;
  padding: ${({ $isLiquidGlass }) => ($isLiquidGlass ? '8px 20px 20px' : '8px 20px 50px')};
  position: ${({ $isLiquidGlass }) => ($isLiquidGlass ? 'absolute' : 'static')};
  bottom: ${({ $isLiquidGlass }) => ($isLiquidGlass ? '30px' : 'auto')};
  left: ${({ $isLiquidGlass }) => ($isLiquidGlass ? '3%' : 'auto')};
  width: ${({ $isLiquidGlass }) => ($isLiquidGlass ? '94%' : '100%')};
`;

const TabIconContainer = styled.div<{ color?: string }>`
  align-items: center;
  color: ${({ color }) => (color?.length ? color : 'inherit')};
  display: flex;
  flex-direction: column;
  font-weight: ${({ color }) => (color ? 'bold' : 'normal')};
  justify-content: center;

  > svg {
    margin-bottom: 3px;
  }
`;

export default TabBar;
